import React, { useState } from 'react'
import InputMask from 'react-input-mask'
import DatePicker from 'react-datepicker'
import axios from 'axios'
import { t, locale } from 'i18n'
import SearchResult from './InheritanceCheckResult'
import MediaQueries from '../utilityComponents/MediaQueries'
import InputWrapper from '../utilityComponents/InputWrapper'
import Alert from '../utilityComponents/Alert'
import { RequiredFieldsLegend } from '../utilityComponents/RequiredMark'

const scope = 'components.inheritance_check'

// The search responds with { error } or, when the registry is unavailable, a plain string
export const searchErrorMessage = (errors) => {
  if (errors.error) return errors.error
  if (typeof errors === 'string') return t('service_unavailable', { scope })
}

const PersonalCodeSearch = ({ searchForm, errors, setFieldValue }) => {
  return (
    <InputWrapper errorArray={errors.personal_code} label={t('personal_code', { scope })} htmlFor='personalCode' required>
      <InputMask
        type='text'
        className='form-control'
        id='personalCode'
        name='personalCode'
        mask='999999-99999'
        onChange={(e) => setFieldValue(e.target.value, 'personalCode')}
        placeholder={t('personal_code', { scope })}
        value={searchForm.personalCode}
      />
    </InputWrapper>
  )
}

const NameSearch = ({ searchForm, errors, setFieldValue, isMobile }) => {
  return (
    <>
      <InputWrapper errorArray={errors.first_name} label={t('first_name', { scope })} htmlFor='firstName' required>
        <input
          type='text'
          className='form-control'
          id='firstName'
          name='firstName'
          onChange={(e) => setFieldValue(e.target.value, 'firstName')}
          placeholder={t('first_name', { scope })}
          value={searchForm.firstName} />
      </InputWrapper>
      <InputWrapper errorArray={errors.last_name} label={t('last_name', { scope })} htmlFor='lastName' required>
        <input
          type='text'
          className='form-control'
          id='lastName'
          name='lastName'
          onChange={(e) => setFieldValue(e.target.value, 'lastName')}
          placeholder={t('last_name', { scope })}
          value={searchForm.lastName} />
      </InputWrapper>
      <div className='row'>
        <div className='col-sm-6'>
          <InputWrapper className='datepicker' errorArray={errors.date_of_birth_from} label={t('date_of_birth_from', { scope })} htmlFor='birthDateFrom' required>
            <DatePicker
              className='form-control'
              id='birthDateFrom'
              dateFormat={t('date.formats.moment')}
              placeholderText={t('date_of_birth_from', { scope })}
              selected={searchForm.dateOfBirthFrom}
              onChange={(e) => setFieldValue(e, 'dateOfBirthFrom')}
              showMonthDropdown
              showYearDropdown
              dropdownMode='select'
              todayButton={t('components.datepicker.today')}
              customInput={<InputMask mask='99.99.9999.'/>}
              withPortal={isMobile}
              locale={locale}
              name='birthDateFrom'
            />
          </InputWrapper>
        </div>
        <div className='col-sm-6'>
          <InputWrapper className='datepicker' errorArray={errors.date_of_birth_to} label={t('date_of_birth_to', { scope })} htmlFor='birthDateTo' required>
            <DatePicker
              className='form-control'
              id='birthDateTo'
              dateFormat={t('date.formats.moment')}
              placeholderText={t('date_of_birth_to', { scope })}
              selected={searchForm.dateOfBirthTo}
              onChange={(e) => setFieldValue(e, 'dateOfBirthTo')}
              showMonthDropdown
              showYearDropdown
              dropdownMode='select'
              todayButton={t('components.datepicker.today')}
              customInput={<InputMask mask='99.99.9999.'/>}
              withPortal={isMobile}
              locale={locale}
              name='birthDateTo'
            />
          </InputWrapper>
        </div>
      </div>
    </>
  )
}

const InheritanceSearch = ({
  searchType, searchForm, errors, setFieldValue, isMobile, submitForm, loading
}) => {
  return (
    <>
      {searchType === 'personal_code'
        ? <PersonalCodeSearch
            searchForm={searchForm}
            errors={errors}
            setFieldValue={setFieldValue}
          />
        : <NameSearch
            searchForm={searchForm}
            errors={errors}
            setFieldValue={setFieldValue}
            isMobile={isMobile}
          />
      }
      <RequiredFieldsLegend />
      <div className='text-center'>
        <button type='button'
          className='btn btn-primary btn-lg mt-3 mb-4'
          onClick={submitForm}
        >
          <ButtonContent loading={loading} buttonText={t('search')}/>
        </button>
      </div>
    </>
  )
}

export const ButtonContent = ({ loading, buttonText }) => {
  if (loading) {
    return (
      <span>
        {t('search_in_progress')}
        <span className='loading-indicator small'></span>
      </span>
    )
  } else {
    return <>{buttonText}</>
  }
}

const initialSearch = {
  personalCode: '',
  firstName: '',
  lastName: '',
  dateOfBirthFrom: null,
  dateOfBirthTo: null
}

const InheritanceCheckForm = ({ isMobile }) => {
  const [searchType, setSearchType] = useState('personal_code')
  const [searchForm, setSearchForm] = useState(initialSearch)

  const [searchResult, setSearchResult] = useState(null)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value)
    clearSearch()
  }

  const clearSearch = () => {
    setSearchResult(null)
    setSearchForm(initialSearch)
  }

  const setFieldValue = (e, fieldName) => {
    setSearchForm({...searchForm, [fieldName]: e})
  }

  const submitForm = () => {
    setLoading(true)
    axios.post('/inheritance_checks/search', {
      inheritance_check: {
        search_type: searchType,
        personal_code: searchForm.personalCode,
        first_name: searchForm.firstName,
        last_name: searchForm.lastName,
        date_of_birth_from: searchForm.dateOfBirthFrom?.format('DD.MM.yyyy'),
        date_of_birth_to: searchForm.dateOfBirthTo?.format('DD.MM.yyyy'),
      }})
      .then(({ data }) => {
        setLoading(false)
        setSearchResult(data.matters)
        setErrors({})
      })
      .catch(({ response }) => {
        setLoading(false)
        setSearchResult(null)
        setErrors(response.data)
      })
  }

  const NoResultsMessage = () => {
    if(searchResult === null || searchResult.length > 0) return null
    return <Alert type='info'>{t('no_results', { scope })}</Alert>
  }

  return (
    <div className='service-form service-form-wide inheritance-check-form'>
      <Alert>{searchErrorMessage(errors)}</Alert>
      <NoResultsMessage/>
      {searchResult?.length > 0 &&
        <SearchResult
          matterResults={searchResult}
          searchForm={searchForm}
          searchType={searchType}
        />
      }
      {!searchResult?.length &&
        <>
          <div className='inheritance-search-type mb-3'>
            <label className='me-4'>
              <input type='radio'
                value='personal_code'
                id='search-personal-code'
                className='me-2'
                onChange={handleSearchTypeChange}
                checked={searchType === 'personal_code'}
              />
              {t('search_personal_code', { scope })}
            </label>
            <label>
              <input type='radio'
                value='name'
                id='search-name'
                className='me-2'
                onChange={handleSearchTypeChange}
                checked={searchType === 'name'}
              />
              {t('search_name', { scope })}
            </label>
          </div>
          <InheritanceSearch
            searchType={searchType}
            searchForm={searchForm}
            errors={errors}
            setFieldValue={setFieldValue}
            isMobile={isMobile}
            submitForm={submitForm}
            loading={loading}
          />
        </>
      }
    </div>
  )
}

export default MediaQueries(InheritanceCheckForm)
